import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";

const faqs: FAQItem[] = [
  {
    question: "Does SAINT treat anxiety, or only depression?",
    answer:
      "SAINT is FDA-cleared for major depressive disorder, not for anxiety disorders as a standalone diagnosis. But because the targeted circuit regulates both mood and the threat response, patients treated for depression commonly report that anxiety, panic, and avoidance ease as the depression lifts. Clinically, depression is what's treated; the anxiety typically follows.",
  },
  {
    question: "Do I need to have failed five medications to qualify for SAINT?",
    answer:
      "No. The indication is one adequate medication trial without adequate relief. You don't have to spend another year cycling through drug classes to qualify. If you've had one or more adequate trials and are still depressed, you meet the definition for treatment-resistant depression and qualify for evaluation for SAINT.",
  },
  {
    question: "Can I keep taking my current medications during SAINT?",
    answer:
      "Yes. SAINT doesn't require stopping anything, and nothing enters the bloodstream, so there's no interaction with your current medications. Many patients do eventually simplify their regimen, but only after treatment and only with their prescriber, over time. Do not stop any medication without talking to your prescriber first.",
  },
];

const related: RelatedLink[] = [
  {
    title: "The Executive Who Can't Afford Six Weeks",
    href: "/the-executive-who-cant-afford-six-weeks",
    description:
      "Why the standard path doesn't fit a high performer's life, and what does.",
  },
  {
    title:
      "You've Tried Everything Money Can Buy. Here's What You Haven't Tried.",
    href: "/youve-tried-everything-money-can-buy",
    description:
      "Why the best-resourced patients often end up in the most outdated care, and where the real frontier is.",
  },
  {
    title: "How to Do Due Diligence on a Depression Treatment",
    href: "/how-to-do-due-diligence-on-a-depression-treatment",
    description:
      "A framework for evaluating any depression treatment, using SAINT as the worked example.",
  },
];

const comparisonRows = [
  {
    feature: "Mechanism",
    columns: [
      "Chemistry",
      "Chemistry (glutamate)",
      "Circuitry",
      "Circuitry",
      "Circuitry, image-guided",
    ],
  },
  {
    feature: "Time to judge",
    columns: [
      "6–12 weeks",
      "Weeks, then ongoing",
      "6–9 weeks",
      "3 weeks",
      "5 days",
    ],
  },
  {
    feature: "Remission (TRD)",
    columns: ["~14%", "16–37%", "17–32%", "48%", "79%"],
  },
];

export default function AnxietyTurnsIntoDepressionFoundersGuide() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "When Anxiety Turns Into Depression: A Guide for Founders & Executives",
        description:
          "High performers have a specific relationship with anxiety. When it stops being fuel and starts working against you, it often converts into depression. Here's the progression, the treatment options compared, and what the most advanced option looks like from first call to final session.",
        canonical:
          "/when-anxiety-turns-into-depression-a-guide-for-founders-and-executives",
        keywords:
          "anxiety turns into depression, founder depression, executive depression, treatment-resistant depression, SAINT TMS, anticipatory anxiety, high performer depression, fMRI-guided TMS, depression treatment comparison",
      }}
      badge="For High Performers"
      heroTitle="When Anxiety Turns Into Depression: A Guide for Founders and Executives Who Used to Be the Strong One"
      heroSubtitle="High performers have a specific relationship with anxiety. A certain amount of it is fuel. Then, for some people, it stops working and starts working against them. And somewhere in that stretch, the anxiety converts into depression."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                High performers have a specific relationship with anxiety. A
                certain amount of it is fuel. It sharpens a pitch, keeps a deal
                on track, and pushes through the last week before a launch. For
                years, it works.
              </p>
              <p>
                Then, for some people, it stops working and starts working
                against them. Anxiety that used to be situational becomes
                constant. Panic attacks appear, recede, and return. Sleep
                breaks. Medication gets tried and either does nothing or makes
                things worse. And somewhere in that stretch, the anxiety
                converts into depression: the loss of joy, the fear of things
                that used to be pleasures, the sense of mourning a self that
                seems to have vanished.
              </p>
              <p>
                This guide explains that progression, why it hits founders and
                executives in a recognizable pattern, what the treatment options
                are, and what the most advanced option looks like from first
                call to final session.
              </p>
            </div>
          ),
        },
        {
          heading: "How anxiety becomes depression",
          body: (
            <div className="space-y-5">
              <p>
                Anxiety and depression share circuitry. The region behind the
                forehead called the dorsolateral prefrontal cortex regulates
                mood and also acts as a brake on the brain's threat and worry
                system. Under prolonged stress, that region becomes underactive.
                Two things happen at once: the brake fails, so worry runs
                unchecked, and mood regulation fails, so the low sets in.
              </p>
              <p>
                For someone whose baseline was confident and outgoing, the
                sequence often looks like this:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Sustained overload.</strong> Years of high
                  responsibility with no real release. Stress accumulates rather
                  than resolving.
                </li>
                <li>
                  <strong>A tipping event.</strong> A health change, a
                  medication change, a financial setback, a loss. Sometimes
                  nothing identifiable. The accumulated load crosses a
                  threshold.
                </li>
                <li>
                  <strong>Anxiety and panic.</strong> Initially episodic, then
                  persistent.
                </li>
                <li>
                  <strong>Anticipatory anxiety.</strong> Fear of the next
                  episode leads to avoidance. Invitations, travel, social
                  events, and public roles become sources of dread rather than
                  pleasure. The world contracts.
                </li>
                <li>
                  <strong>Depression.</strong> Loss of interest and pleasure,
                  exhaustion, cognitive fog, and a grief-like awareness of
                  what's been lost.
                </li>
              </ul>
              <p>
                If there was an earlier episode years ago that resolved, this
                second one is often more severe, and the person recognizes the
                descent as it's happening.
              </p>
            </div>
          ),
        },
        {
          heading: "Why founders and executives are particularly exposed",
          body: (
            <div className="space-y-5">
              <p>
                <strong>The barrel model.</strong> Stress accumulates like water
                in a barrel. High performers keep the level just below the rim
                for years by draining it through achievement and momentum. When
                something raises the level, the overflow isn't stress. It's the
                underlying vulnerability that was managed, not treated.
              </p>
              <p>
                <strong>Identity built on capability.</strong> When self-worth
                depends on being the decisive one, impairment in the
                decision-making circuitry is doubly destabilizing. The person
                loses the function and the identity at once.
              </p>
              <p>
                <strong>High-functioning masking.</strong> Executives are
                skilled at performing competence while deteriorating. Clinicians
                treat conservatively because the patient looks fine. Partial
                improvement gets accepted as success.
              </p>
              <p>
                <strong>Self-imposed pressure.</strong> The expectations others
                hold are usually lower than the ones the person holds for
                themselves. There is no external pressure to reduce, because the
                pressure is internal.
              </p>
            </div>
          ),
        },
        {
          heading: "Warning signs specific to this population",
          body: (
            <div className="space-y-2">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Anticipatory dread about events that were previously
                  enjoyable: dinners, speaking engagements, travel, family
                  occasions
                </li>
                <li>
                  Declining invitations and delegating public roles without
                  explanation
                </li>
                <li>
                  Decision paralysis on matters that used to be resolved in
                  minutes
                </li>
                <li>
                  Ruminative loops: the same concern revisited repeatedly
                  without resolution
                </li>
                <li>
                  Morning symptoms that are markedly worse than afternoon or
                  evening
                </li>
                <li>
                  Physical symptoms: head pressure, headaches, gastrointestinal
                  disruption, exhaustion by mid-morning
                </li>
                <li>
                  Emotional numbness after starting medication, or worsening
                  rather than improvement
                </li>
                <li>A stated sense of missing one's former self</li>
              </ul>
            </div>
          ),
        },
        {
          heading: "What to do about medication that isn't working",
          body: (
            <div className="space-y-5">
              <p>
                Antidepressants adjust brain chemistry, and for a majority of
                people that's sufficient. But they take four to eight weeks to
                reach full effect, they can flatten emotion before they improve
                mood, and in some individuals, particularly early in treatment,
                they intensify anxiety or produce bleak mornings that are worse
                than the baseline.
              </p>
              <p>
                If a medication makes you feel less like yourself rather than
                more, that is clinically important. Report it to your prescriber
                immediately. Do not stop on your own, and do not assume you're
                obligated to push through. If a medication produces any thoughts
                of self-harm, contact your prescriber the same day.
              </p>
              <p>
                If you've had one or more adequate medication trials without
                adequate relief, you meet the definition for treatment-resistant
                depression, and you qualify for evaluation for circuitry-based
                treatment. You don't need to have failed five medications first.
              </p>
            </div>
          ),
        },
        {
          heading: "Treatment options, compared",
          body: (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-2 pr-4 font-bold text-primary">
                        &nbsp;
                      </th>
                      <th className="text-left py-2 px-2 font-bold text-primary">
                        Another antidepressant
                      </th>
                      <th className="text-left py-2 px-2 font-bold text-primary">
                        Esketamine
                      </th>
                      <th className="text-left py-2 px-2 font-bold text-primary">
                        Standard TMS
                      </th>
                      <th className="text-left py-2 px-2 font-bold text-primary">
                        ECT
                      </th>
                      <th className="text-left py-2 px-2 font-bold text-accent">
                        SAINT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row) => (
                      <tr
                        key={row.feature}
                        className="border-b border-border/50"
                      >
                        <td className="py-2 pr-4 font-semibold text-primary">
                          {row.feature}
                        </td>
                        {row.columns.map((col, i) => (
                          <td
                            key={i}
                            className={`py-2 px-2 ${i === 4 ? "font-bold text-accent" : "text-muted-foreground"}`}
                          >
                            {col}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="py-2 pr-4 font-semibold text-primary">
                        Notes
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">
                        Systemic side effects
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">
                        Dissociation; maintenance dosing
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">
                        30–36 daily visits; no anesthesia
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">
                        General anesthesia; memory risk
                      </td>
                      <td className="py-2 px-2 text-xs font-semibold text-accent">
                        fMRI-targeted; no anesthesia, no drugs
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground italic">
                Figures drawn from published studies in treatment-resistant
                depression and related populations. Study designs vary;
                individual results vary.
              </p>
            </div>
          ),
        },
        {
          heading: "SAINT: the technology, and the company behind it",
          body: (
            <div className="space-y-5">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                was developed at Stanford's Brain Stimulation Lab by
                psychiatrists and neuroscientists studying why depression fails
                to respond to medication. Their published trials established the
                protocol; Magnus Medical, a California-based medical technology
                company, licensed it and built the SAINT Neuromodulation System
                that delivers it in clinical practice. The system received FDA
                Breakthrough Device Designation and was cleared in 2022 for
                adults with major depressive disorder who haven't gotten
                adequate relief from antidepressants in their current episode.
              </p>
              <p>
                Magnus Medical doesn't treat patients directly. It designates
                clinics that have the required equipment, training, and quality
                standards as{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Providers
                </Link>
                , and publishes its clinical data, provider directory, and
                patient resources at magnusmedical.com. Neuro Wellness TMS
                Centers of America is a Verified SAINT Provider.
              </p>
            </div>
          ),
        },
        {
          heading: "How it applies to the founder in a spiral",
          body: (
            <div className="space-y-5">
              <p>
                The progression described at the top of this guide — overload to
                anxiety to avoidance to depression — is a circuitry story. The
                dorsolateral prefrontal cortex, which should regulate mood and
                hold the brake on the threat system, has gone underactive. Every
                symptom on the warning-sign list traces back to that: the
                decision paralysis, the dread before events, the morning lows,
                the loops, the fog.
              </p>
              <p>
                Medication works on the chemical environment around that
                circuit. If it hasn't restored you, or has made you feel numb or
                worse, the problem isn't that you need a different chemical.
                It's that the circuit itself needs to be re-engaged. That's
                precisely what SAINT is designed to do, and the population it
                was tested on — adults whose depression had already failed
                medication — is the population you're in.
              </p>
              <p>
                Two features matter especially for this reader. First, the
                indication is one adequate medication trial without adequate
                relief, not five. You don't have to spend another year cycling
                through drug classes to qualify. Second, the five-day schedule
                means the treatment fits inside a week you can block, rather
                than two months of daily appointments you can't.
              </p>
            </div>
          ),
        },
        {
          heading: "How it helps, and what it's realistic to expect",
          body: (
            <div className="space-y-5">
              <p>
                <strong>On the depression.</strong> The primary target. In
                trials, most participants reached remission within the treatment
                week, with the average at day two to three. Patients commonly
                describe the shift as the fog lifting, energy returning, and the
                ability to think clearly again, rather than simply feeling less
                sad.
              </p>
              <p>
                <strong>On the anxiety and anticipatory dread.</strong> SAINT is
                cleared for major depressive disorder, not for anxiety disorders
                as a standalone diagnosis. But because the targeted circuit
                regulates both mood and the threat response, patients treated
                for depression commonly report that anxiety, panic, and
                avoidance ease as the depression lifts. The dinners, the travel,
                the speaking roles stop registering as threats. Clinically,
                depression is what's treated; the anxiety typically follows.
              </p>
              <p>
                <strong>On the physical symptoms.</strong> Head pressure,
                headaches, gastrointestinal disruption, and exhaustion that are
                driven by the depression tend to improve with it. Symptoms with
                a separate medical cause need their own evaluation.
              </p>
              <p>
                <strong>On medication.</strong> SAINT doesn't require stopping
                anything, and nothing enters the bloodstream, so there's no
                interaction. Many patients do eventually simplify their regimen,
                but only after treatment and only with their prescriber, over
                time.
              </p>
              <p>
                <strong>What it doesn't do.</strong> It doesn't guarantee
                remission; roughly one in five trial participants didn't reach
                it. It doesn't remove the stressors that filled the barrel;
                those still need managing, and the daily therapy check-ins
                during treatment are partly about that. And it isn't appropriate
                for everyone: non-removable metal near the head, seizure
                history, and implanted devices are exclusions screened at
                consultation.
              </p>
            </div>
          ),
        },
        {
          heading: "The process, step by step",
          body: (
            <div className="space-y-4">
              <p>
                <strong>1. Initial call.</strong> A conversation with a care
                team member about what's been happening and what you've tried.
                Screening questions: metal in or near the head, seizure history,
                implanted devices. Insurance details, so coverage can be
                checked. No commitment.
              </p>
              <p>
                <strong>2. Clinical intake.</strong> A telehealth appointment,
                typically within one to two days, with a clinician who takes a
                full history and coordinates with the psychiatrist.
              </p>
              <p>
                <strong>3. Psychiatric evaluation.</strong> By video, typically
                within the same week. Confirms the diagnosis, reviews
                medications, and clears you for treatment.
              </p>
              <p>
                <strong>4. Functional MRI.</strong> Roughly 45 minutes at an
                imaging facility. No injected contrast. Results are processed
                through the targeting algorithm.
              </p>
              <p>
                <strong>5. Treatment week.</strong> Five consecutive days;
                weekend days are available. Ten sessions per day, one per hour,
                ten minutes each. The sensation is light tapping on the scalp.
                No anesthesia, no sedation. Between sessions, patients use a
                private suite with a desk, wifi, and a place to rest or work.
                Patients drive themselves home or arrange transport; no one
                leaves impaired. Typical side effects: mild fatigue by the end
                of the day, or a mild headache.
              </p>
              <p>
                <strong>6. Daily therapy check-in.</strong> A therapist meets
                with each patient every day of treatment, to monitor progress
                and to prepare for the transition afterward, including the
                common urge to stop medications immediately upon feeling better.
                Tapering is a decision made with your prescriber, over time.
              </p>
              <p>
                <strong>7. Follow-up.</strong> Your care team will review what
                to expect afterward and what to do if symptoms ever begin to
                return.
              </p>
            </div>
          ),
        },
        {
          heading: "Cost and coverage",
          body: (
            <div className="space-y-5">
              <p>
                Insurance coverage for SAINT varies by plan; some cover it with
                prior authorization, others do not yet. Self-pay is available.
                Coverage is checked before anything is scheduled, and the care
                team explains the options in advance.
              </p>
              <p>
                From first call to completed treatment can be under two weeks
                when schedules allow. For patients with a specific deadline — a
                family event, a board meeting, a commitment they don't want to
                face at half capacity — the care team can often structure the
                intake, imaging, and treatment week around it.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If you've recognized the progression described here — from fuel
                to dread to fear of the things you love — and medication hasn't
                restored you, you're a candidate for evaluation.
              </p>
              <p>
                Neuro Wellness TMS Centers of America is a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                offering private outpatient care with a dedicated care team,
                weekend availability, and assistance with nearby accommodations
                for patients traveling in. The first call commits you to
                nothing.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                Learn more:{" "}
                <a
                  href="/saint"
                  className="text-primary font-semibold hover:underline"
                >
                  neurowellnesstms.com/saint
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="The progression from fuel to dread to fear of the things you love is a circuitry story. The circuit can be re-engaged. One week, not two months."
    />
  );
}
